import { useState } from "react";
import api, { formatApiError, formatDate } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Sparkle, CircleNotch } from "@phosphor-icons/react";
import { RISK_STYLES, RISK_LABELS } from "../badges";

export default function AiAnalysisSection({ claim, canRun, onChanged }) {
  const [running, setRunning] = useState(false);
  const analysis = claim.ai_analysis;
  const result = analysis?.result;

  const run = async () => {
    setRunning(true);
    try {
      await api.post(`/claims/${claim.id}/ai-analysis`, { model: "gpt-5.2" });
      toast.success("Analisis FraudGuard AI selesai");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  };

  if (!canRun && !analysis) return null;

  return (
    <div data-testid="ai-analysis-section">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
          <Sparkle size={14} /> Analisis FraudGuard AI
        </h3>
        {canRun && (
          <Button data-testid="run-ai-analysis-button" size="sm" disabled={running} onClick={run}
            className="bg-slate-900 hover:bg-slate-800 rounded-none h-8 gap-2">
            {running ? <><CircleNotch size={14} className="animate-spin" /> Menganalisis...</> : analysis ? "Analisis Ulang" : "Jalankan"}
          </Button>
        )}
      </div>

      {!analysis && <p className="text-sm text-muted-foreground border border-dashed border-border p-3">Belum dianalisis. Jalankan FraudGuard AI untuk analisis mendalam kronologi memakai aturan, phrase dictionary, dan ketentuan polis sebagai konteks.</p>}

      {result && (
        <div className="border border-border/60 p-4 space-y-3" data-testid="ai-analysis-result">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="font-mono text-2xl font-bold" data-testid="ai-score">{result.skor_ai}<span className="text-sm text-muted-foreground">/100</span></span>
              <span className={`inline-flex px-2 py-0.5 text-xs font-semibold border ${RISK_STYLES[result.tingkat_kecurigaan] || ""}`}>
                {RISK_LABELS[result.tingkat_kecurigaan] || result.tingkat_kecurigaan}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">FraudGuard AI · {analysis.analyzed_by} · {formatDate(analysis.analyzed_at)}</p>
          </div>
          <p className="text-sm leading-relaxed">{result.ringkasan}</p>
          {result.indikasi?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Indikasi Fraud</p>
              <div className="space-y-1.5">
                {result.indikasi.map((it, i) => (
                  <div key={`${it.judul}-${i}`} className="border-l-2 border-red-300 pl-3">
                    <p className="text-sm font-medium">{it.judul}</p>
                    <p className="text-xs text-muted-foreground">{it.penjelasan}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.pelanggaran_psakbi?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Potensi Pelanggaran PSAKBI</p>
              <div className="space-y-1.5">
                {result.pelanggaran_psakbi.map((p, i) => (
                  <div key={`${p.pasal}-${i}`} data-testid={`psakbi-violation-${i}`} className="border border-red-200 bg-red-50 p-2.5">
                    <p className="text-sm font-semibold text-red-800">{p.pasal} — {p.indikasi}</p>
                    <p className="text-xs text-red-700 mt-0.5">{p.penjelasan}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.inkonsistensi?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Inkonsistensi Naratif</p>
              <ul className="list-disc list-inside text-sm space-y-0.5">
                {result.inkonsistensi.map((s, i) => <li key={`${i}-${s.slice(0, 40)}`}>{s}</li>)}
              </ul>
            </div>
          )}
          {result.saran_investigasi?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Saran Investigasi</p>
              <ul className="list-disc list-inside text-sm space-y-0.5">
                {result.saran_investigasi.map((s, i) => <li key={`${i}-${s.slice(0, 40)}`}>{s}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
